import type { ReactNode } from "react";

import { Flex, Grid, Text } from "@radix-ui/themes";

import {
  deriveHeaderPortfolioPercentages,
  type HeaderPortfolioTotals,
} from "./headerPortfolioMetrics.utils";

export interface HeaderPortfolioMetricsPresentationProps {
  totals: HeaderPortfolioTotals | null;
  isLoading: boolean;
}

const moneyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const percentFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatPnlMoney = (value: number): string =>
  value > 0 ? `+${moneyFormatter.format(value)}` : moneyFormatter.format(value);

const formatPnlPercent = (value: number | null): string => {
  if (value === null) {
    return "—";
  }
  return value > 0
    ? `+${percentFormatter.format(value)}`
    : percentFormatter.format(value);
};

const pnlColor = (value: number): "green" | "red" | "gray" => {
  if (value > 0) {
    return "green";
  }
  if (value < 0) {
    return "red";
  }
  return "gray";
};

interface MetricProps {
  label: string;
  children: ReactNode;
}

const Metric = ({ label, children }: MetricProps) => (
  <Flex direction="column" align="start" gap="1">
    <Text
      size="1"
      color="gray"
      weight="medium"
      className="whitespace-nowrap uppercase tracking-[0.06em]"
    >
      {label}
    </Text>
    {children}
  </Flex>
);

const Placeholder = () => (
  <Text size="2" color="gray" className="atd-numeric">
    —
  </Text>
);

interface PnlMetricProps {
  testId: string;
  value: number;
  percentage: number | null;
}

const PnlMetric = ({ testId, value, percentage }: PnlMetricProps) => {
  const color = pnlColor(value);
  return (
    <Flex align="center" justify="start" gap="2">
      <Text
        size="2"
        weight="bold"
        color={color}
        className="atd-numeric"
        data-testid={testId}
      >
        {formatPnlMoney(value)}
      </Text>
      <Text size="1" color={color} className="atd-numeric">
        {formatPnlPercent(percentage)}
      </Text>
    </Flex>
  );
};

export const HeaderPortfolioMetricsPresentation = ({
  totals,
  isLoading,
}: HeaderPortfolioMetricsPresentationProps) => {
  const showPlaceholder = isLoading || totals === null;
  const percentages = totals
    ? deriveHeaderPortfolioPercentages(totals)
    : { todayPct: null, totalPct: null };

  return (
    <Grid
      columns="repeat(4, minmax(0, 1fr))"
      gap="5"
      data-testid="header-portfolio-metrics"
    >
      <Metric label="EQUITY">
        {showPlaceholder || totals.equity === null ? (
          <Placeholder />
        ) : (
          <Text size="2" weight="bold" className="atd-numeric">
            {moneyFormatter.format(totals.equity)}
          </Text>
        )}
      </Metric>
      <Metric label="TODAY'S P&L">
        {showPlaceholder ? (
          <Placeholder />
        ) : (
          <PnlMetric
            testId="today-pnl"
            value={totals.todaysPnl}
            percentage={percentages.todayPct}
          />
        )}
      </Metric>
      <Metric label="TOTAL P&L">
        {showPlaceholder ? (
          <Placeholder />
        ) : (
          <PnlMetric
            testId="total-pnl"
            value={totals.totalPnl}
            percentage={percentages.totalPct}
          />
        )}
      </Metric>
      <Metric label="BUYING POWER">
        {showPlaceholder || totals.buyingPower === null ? (
          <Placeholder />
        ) : (
          <Text size="2" weight="bold" className="atd-numeric">
            {moneyFormatter.format(totals.buyingPower)}
          </Text>
        )}
      </Metric>
    </Grid>
  );
};
